import type { HttpPort, ISettingsService, Json } from "../contracts";

// Wire shapes — mirror the legacy `src/frontend/src/workspace/settings/
// types.ts` 1:1 so the backend payload doesn't need any schema
// negotiation. Backend declares these via the <settings> XML DSL +
// `core.services.settings.registry` + `foundation.base.api.settings_routes`.

export interface ModuleNavEntry {
    module: string;
    label: string;
    icon: string | null;
    category: string;
    order: number;
}

export type SettingsFieldType = "boolean" | "integer" | "char" | "selection" | "many2one";
export type SettingsScope = "system" | "organization";

export interface SettingsFieldOption {
    value: string;
    label: string;
}

export interface SettingsFieldDef {
    name: string;
    type: SettingsFieldType;
    scope: SettingsScope;
    label: string;
    help: string | null;
    requires_migration: boolean;
    secret: boolean;
    depends_on: string | null;
    readonly: boolean;
    options: SettingsFieldOption[];
    model: string | null;
    domain: string | null;
    /** When set, the value is read/written directly on
        `res.organization.<related_field>` for the active org instead of
        an `ir.config` row — the Settings UI is then a typed view over
        the org column. */
    related_field: string | null;
    value: unknown;
    display_value: string | null;
}

export interface SettingsButtonDef {
    name: string;
    label: string;
    /** Either a legacy verb (`"open_form"`) or a `module.xml_id`
        reference to an `ir.action` row (action-driven path, recommended). */
    action: "open_form" | string;
    /** Legacy `open_form`: target model_key. Ignored for action-driven. */
    model: string | null;
    /** Name of the sibling <field> on the same panel whose current
        draft value is the record_uuid to pass into the action / open. */
    record_ref_field: string | null;
    help: string | null;
    icon: string | null;
    /** Resolved `ir.action.path` slug — set by the backend at panel-load
        time for action-driven buttons (action contains a dot). null for
        legacy verb buttons OR when the xml_id couldn't be resolved (the
        frontend surfaces a clear toast in that case instead of dying). */
    action_slug: string | null;
}

export interface SettingsSectionDef {
    string: string;
    description: string | null;
    full_width: boolean;
    card_view: boolean;
    fields: SettingsFieldDef[];
    buttons: SettingsButtonDef[];
}

export interface ModuleSettings {
    module: string;
    label: string;
    icon: string | null;
    sections: SettingsSectionDef[];
}

const BASE = "/api/settings";

export class SettingsService implements ISettingsService {
    constructor(private readonly http: HttpPort) {}

    async listModules(): Promise<ModuleNavEntry[]> {
        const res = await this.http.get<ModuleNavEntry[] | { modules?: ModuleNavEntry[] }>(BASE);
        if (Array.isArray(res)) return res;
        return res?.modules ?? [];
    }

    async readModule(moduleKey: string): Promise<ModuleSettings> {
        return this.http.get<ModuleSettings>(`${BASE}/${encodeURIComponent(moduleKey)}`);
    }

    async writeModule(moduleKey: string, values: Record<string, Json>): Promise<ModuleSettings> {
        return this.http.patch<ModuleSettings>(`${BASE}/${encodeURIComponent(moduleKey)}`, { values });
    }
}
